import { ReactNode, useEffect, useState } from 'react'
import { ButtonBase, Theme, SxProps } from '@mui/material'
import TableMui from '@mui/material/Table'

enum Format {
  NUMBER = 'Int',
  HEX = 'Hex',
  SIGN = 'Sign',
  STRING = 'String',
  STRING_CONCAT = 'StrConcat',
  PDF = 'PDF',
  XML = 'XML',
  DATE = 'DATE'
}

enum Bloque {
  PRINCIPAL = 'MAIN',
  XML = 'XML',
  ADJUNTO = 'PDF'
}

type CadesData = {
  name: string
  data: string
  offset: number
  size: number
  format: Format
  bytesData: ArrayBuffer
  area: Bloque
  int8: Int8Array
}

type BloquePrincipal = {
  firmaCabecera: CadesData
  versionCades: CadesData
  sinUso: CadesData
  longuitudCades: CadesData
  numFicheros: CadesData
  fechaMod: CadesData
  longuitudFirma: CadesData
  firmaCades: CadesData
}

type BloqueAdjunto = {
  firmaCabecera: CadesData
  numero: CadesData
  tipo: CadesData
  formato: CadesData
  longuitud: CadesData
  longuitudNombre: CadesData
  longuitudFirma: CadesData
  nombreFichero: CadesData
  firmaFichero: CadesData
  fichero: CadesData
}

type CadesFile = {
  bloquePrincipal: BloquePrincipal
  bloqueXML: BloqueAdjunto
  bloqueAdjunto: BloqueAdjunto[]
}

enum CadesArea {
  PRINCIPAL = 6,
  FIRMA_CADES = 7,
  XML = 14,
  NOMBRE_FICHERO_XML = 15,
  FIRMA_XML = 16,
  FICHERO_XML = 17,
  ADJUNTO_PRINCIPAL = 23,
  NOMBRE_FICHERO_PDF = 24,
  FIRMA_PDF = 25,
  FICHERO_PDF = 26
}

const styles: SxProps<Theme> = {
  '& td': {
    border: '1px solid',
    padding: '10px'
  },
  '& textarea': {
    width: '100%'
  },
  '& .cades-data': {
    padding: '5px',
    background: 'aliceblue',
    borderRadius: '10px',
    border: '1px solid'
  },
  '& button': {
    border: '1px solid',
    padding: '10px',
    borderRadius: '3px',
    boxShadow: '1px 1px rgb(1 1 1 / 20%)'
  },
  '& button:hover': {
    background: 'rbg(200 1 1 /20%)'
  }
}

function Cades(): JSX.Element {
  const [cadesFile, setCadesFile] = useState<File | null>(null)
  const [fileSize, setFileSize] = useState(0)
  const [texto, setTexto] = useState('')
  const [cades, setCades] = useState<CadesFile | null>(null)
  const hexChar = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F']

  function byteToHex(b): string {
    /* eslint no-bitwise: ["error", { "allow": ["&", ">>"] }] */
    return hexChar[(b >> 4) & 0x0f] + hexChar[b & 0x0f]
  }
  const formats = [
    Format.HEX,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.DATE,
    Format.NUMBER,
    Format.SIGN
  ]

  const formatsXML = [
    Format.HEX,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.STRING,
    Format.SIGN,
    Format.XML
  ]

  const formatsAdjuntos = [
    Format.HEX,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.NUMBER,
    Format.STRING,
    Format.SIGN,
    Format.PDF
  ]

  function format(byteArray: ArrayBuffer, formatType: Format): string {
    const data = new Int8Array(byteArray)
    let hex = ''
    let string = ''

    data.forEach(value => {
      hex += byteToHex(value)
      string += String.fromCharCode(value)
    })

    // date = dateObject.toLocaleString()
    let date = new Date()

    switch (formatType) {
      case Format.SIGN:
      case Format.HEX:
        return hex
      case Format.NUMBER:
        return parseInt(hex, 16).toString()
      case Format.XML:
        return atob(string)
      case Format.PDF:
      case Format.STRING:
        return string
      case Format.DATE:
        date = new Date(parseInt(hex, 16))
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
      default:
        return ''
    }
  }

  const showFile = (bytes: ArrayBuffer) => {
    let i = 0
    const size = bytes.byteLength
    const offsetPrincipal = [0, 4, 2, 2, 4, 2, 8, 4, 0]
    const offsetAdjunto = [4, 2, 2, 2, 4, 2, 4, 0, 0]
    const byteSize = [4, 2, 2, 4, 2, 8, 4]
    const byteSizeAdjunto = [4, 2, 2, 2, 4, 2, 4]
    const bloquePrincipal = [
      'firmaCabecera',
      'versionCades',
      'sinUso',
      'longuitudCades',
      'numFicheros',
      'fechaMod',
      'longuitudFirma',
      'firmaCades'
    ]
    const bloqueAdjunto = [
      'firmaCabecera',
      'numero',
      'tipo',
      'formato',
      'longuitud',
      'longuitudNombre',
      'longuitudFirma',
      'nombreFichero',
      'firmaFichero',
      'fichero'
    ]

    const titles = [
      'Firma Cabecera',
      'Version CADES',
      'Sin uso',
      'Longuitud Cades',
      'Número de ficheros',
      'Fecha de modificación',
      'Longuitud Firma',
      'Firma CADES'
    ]

    const titlesAdjunto = [
      'Firma Cabecera',
      'Número de adjunto',
      'Tipo de adjunto',
      'Formato',
      'Longuitud adjunto',
      'Longuitud nombre',
      'Longuitud Firma',
      'Nombre fichero',
      'Firma adjunto',
      'Fichero'
    ]

    const cadesBlock: CadesFile = {
      bloquePrincipal: {} as BloquePrincipal,
      bloqueXML: {} as BloqueAdjunto,
      bloqueAdjunto: [] as BloqueAdjunto[]
    } as CadesFile

    let index = 0
    let indexAdjunto = 0
    let offset = offsetPrincipal[index]
    let isAdjunto = false
    let idAdjunto = 0

    for (i = 0; i < size; i += offset) {
      let field: CadesData = {} as CadesData
      let title = titles[index]
      let fieldFormat = formats[index]
      let readSize = byteSize[index]
      let bloque: Bloque = Bloque.PRINCIPAL

      // console.log('CADES BLOCK', cadesBlock)

      if (index <= CadesArea.FIRMA_CADES) {
        cadesBlock.bloquePrincipal[bloquePrincipal[index]] = {} as CadesData
        field = cadesBlock.bloquePrincipal[bloquePrincipal[index]]
      } else {
        title = titlesAdjunto[indexAdjunto]
        readSize = byteSizeAdjunto[indexAdjunto]
        if (!isAdjunto) {
          bloque = Bloque.XML
          fieldFormat = formatsXML[indexAdjunto]
          cadesBlock.bloqueXML[bloqueAdjunto[indexAdjunto]] = {} as CadesData
          field = cadesBlock.bloqueXML[bloqueAdjunto[indexAdjunto]]
        } else {
          bloque = Bloque.ADJUNTO
          fieldFormat = formatsAdjuntos[indexAdjunto]
          // console.log('BLOQUE ADJUNTO', cadesBlock.bloqueAdjunto)
          cadesBlock.bloqueAdjunto[idAdjunto][bloqueAdjunto[indexAdjunto]] = {} as CadesData
          field = cadesBlock.bloqueAdjunto[idAdjunto][bloqueAdjunto[indexAdjunto]]
        }
      }

      if (index <= CadesArea.PRINCIPAL) {
        offset = offsetPrincipal[index + 1]
      } else if (index === CadesArea.FIRMA_CADES) {
        readSize = Number(cadesBlock.bloquePrincipal.longuitudFirma.data)
        offset = readSize
        indexAdjunto = -1
        cadesBlock.bloqueAdjunto = [...cadesBlock.bloqueAdjunto, {} as BloqueAdjunto]
      } else if (index <= CadesArea.XML) {
        offset = offsetAdjunto[indexAdjunto]
      } else if (
        index === CadesArea.NOMBRE_FICHERO_XML ||
        (index > CadesArea.NOMBRE_FICHERO_XML && index % 9 === 6)
      ) {
        readSize = !isAdjunto
          ? Number(cadesBlock.bloqueXML.longuitudNombre.data)
          : Number(cadesBlock.bloqueAdjunto[idAdjunto].longuitudNombre.data)
        offset = readSize
      } else if (
        index === CadesArea.FIRMA_XML ||
        (index > CadesArea.FIRMA_XML && index % 9 === 7)
      ) {
        readSize = !isAdjunto
          ? Number(cadesBlock.bloqueXML.longuitudFirma.data)
          : Number(cadesBlock.bloqueAdjunto[idAdjunto].longuitudFirma.data)
        offset = readSize
      } else if (
        index === CadesArea.FICHERO_XML ||
        (index > CadesArea.FICHERO_XML && index % 9 === 8)
      ) {
        readSize = !isAdjunto
          ? Number(cadesBlock.bloqueXML.longuitud.data)
          : Number(cadesBlock.bloqueAdjunto[idAdjunto].longuitud.data)
        offset = readSize
        indexAdjunto = -1
        index = CadesArea.FIRMA_CADES
        isAdjunto = true
        idAdjunto += 1
        cadesBlock.bloqueAdjunto = [...cadesBlock.bloqueAdjunto, {} as BloqueAdjunto]
      }

      const data = bytes.slice(i, i + readSize)
      const datoCades = format(data, fieldFormat)

      field.name = title
      field.data = datoCades
      field.offset = i
      field.size = readSize
      field.format = fieldFormat
      field.bytesData = data
      field.int8 = new Int8Array(data)
      field.area = bloque

      index += 1
      indexAdjunto += 1
    }

    setCades(cadesBlock)
  }

  const extractFile = () => {
    // console.log('Vamos a leer del fichero', cadesFile)
    if (cadesFile === null) {
      return
    }
    // console.log('El fichero existe')
    const fs = new FileReader()
    fs.readAsArrayBuffer(cadesFile)
    fs.onload = () => {
      const file: ArrayBuffer = fs.result as ArrayBuffer
      setFileSize(file.byteLength)
      showFile(file)
    }
  }

  const selectFile = e => {
    const file = e.target.files[0]
    // console.log(e.target.files[0])
    setCadesFile(file)
  }

  const getDownloadTitle = (format: Format): string => {
    if (format === Format.PDF) return 'Descargar PDF'
    if (format === Format.XML) return 'Descargar XML'
    if (format === Format.SIGN) return 'Descargar Firma'
    return 'Descargar'
  }

  const saveSignAsFile = (data: ArrayBuffer, format: Format) => {
    const bb = new Blob([data], { type: 'text/plain' })
    const a = document.createElement('a')
    if (format === Format.SIGN) a.download = 'firma.csig'
    else if (format === Format.PDF) a.download = 'adjunto.pdf'
    else if (format === Format.XML) a.download = 'adjunto.xml'
    else if (format === Format.STRING) a.download = 'adjunto.txt'

    a.href = window.URL.createObjectURL(bb)
    a.click()
  }

  const getTableHeader = () => {
    return (
      <thead>
        <tr>
          <th style={{ width: 60 }}>Index</th>
          <th style={{ width: 60 }}>Offset</th>
          <th style={{ width: 60 }}>Bytes</th>
          <th style={{ width: 60 }}>Block</th>
          <th style={{ width: 200 }}>Field</th>
          <th style={{ width: 60 }}>Format</th>
          <th>Value</th>
        </tr>
      </thead>
    )
  }

  const getDataNode = (data: string, bytesData: ArrayBuffer, format: Format) => {
    const formats = [Format.SIGN, Format.XML, Format.PDF]
    if (formats.includes(format)) {
      return (
        <>
          <textarea defaultValue={data} />
          <ButtonBase onClick={_e => saveSignAsFile(bytesData, format)}>
            {getDownloadTitle(format)}
          </ButtonBase>
        </>
      )
    }
    return <span className="cades-data">{data}</span>
  }

  const getRecs = bloques => {
    const recs: ReactNode[] = []

    let index = 0

    bloques.forEach(bloque => {
      Object.keys(bloque).forEach(key => {
        recs.push(
          <tr key={`${key}-${index}`}>
            <td>{index}</td>
            <td>{(bloque[key] as CadesData).offset}</td>
            <td>{(bloque[key] as CadesData).size}</td>
            <td>{(bloque[key] as CadesData).area}</td>
            <td>{(bloque[key] as CadesData).name}</td>
            <td>{(bloque[key] as CadesData).format}</td>
            <td>
              {getDataNode(
                (bloque[key] as CadesData).data,
                (bloque[key] as CadesData).bytesData,
                (bloque[key] as CadesData).format
              )}
            </td>
          </tr>
        )
        index += 1
      })
    })

    return recs
  }

  const getTables = () => {
    const tables: ReactNode[] = []
    const bloquePrincipal: BloquePrincipal = cades ? cades.bloquePrincipal : ({} as BloquePrincipal)
    const bloqueXml: BloqueAdjunto = cades ? cades.bloqueXML : ({} as BloqueAdjunto)
    const bloqueAdjunto: BloqueAdjunto[] = cades ? cades.bloqueAdjunto : ([] as BloqueAdjunto[])

    let bloques = [bloquePrincipal, bloqueXml]

    bloqueAdjunto.forEach(bloque => {
      bloques = [...bloques, bloque]
    })

    tables.push(
      <>
        <h2>PRINCIPAL</h2>
        <TableMui sx={styles}>
          {getTableHeader()}
          <tbody style={{ border: '1px solid' }}>{getRecs([bloquePrincipal])}</tbody>
        </TableMui>
      </>
    )
    tables.push(
      <>
        <h2>ADJUNTO XML</h2>
        <TableMui sx={styles}>
          {getTableHeader()}
          <tbody>{getRecs([bloqueXml])}</tbody>
        </TableMui>
      </>
    )
    tables.push(
      <>
        <h2>ADJUNTO PRINCIPAL</h2>
        <TableMui sx={styles}>
          {getTableHeader()}
          <tbody>{getRecs(bloqueAdjunto)}</tbody>
        </TableMui>
      </>
    )

    return tables
  }

  const stringToFile = () => {
    const utf8Encode = new TextEncoder()
    const byteArray = utf8Encode.encode(texto)
    saveSignAsFile(byteArray, Format.STRING)
  }

  useEffect(() => {
    // console.log('EFFECT')
    extractFile()
    // eslint-disable-next-line
  }, [cadesFile])

  return (
    <>
      <h2>CADES MENSAJE</h2>
      <input type="file" name="cadesFile" onChange={selectFile} />
      {cades && (
        <>
          <span>Tamaño (en bytes): {fileSize}</span>
          {getTables()}
        </>
      )}
      <h2>GUARDAR STRING COMO FICHERO</h2>
      <textarea
        onChange={event => {
          setTexto(event.target.value)
        }}
      >
        {texto}
      </textarea>
      <button
        type="button"
        onClick={_e => {
          stringToFile()
        }}
      >
        Guardar
      </button>
    </>
  )
}

export default Cades
